import React from 'react'
import { useHistory } from 'react-router-dom'

import { dataIdFromObject } from '@sketch/graphql-cache'

// External components
import {
  Box,
  Flex,
  Text,
  ErrorMessage,
  TimeAgo,
  handleFetchMore,
  LoadingState,
} from '@sketch/components'
import EditorNavbar from '../../layouts/EditorLayout/EditorNavbar'
import { DesignSystemWithDocs } from '../DesignSystemWithDocs'
import {
  routes,
  RouteProps,
  useFlag,
  GenericErrorView,
} from '@sketch/modules-common'
import ShareNavbar from 'modules/shares/components/ShareNavbar'
import DocumentItemsLayout from 'modules/shares/components/DocumentItemsLayout'
import { DocumentItemDropdown } from 'modules/shares/components/DocumentItemDropdown'
import { DesignSystemDropdown } from '../../components/DesignSystemDropdown'
import { Header } from './Header'
import { ErrorHandler } from '@sketch/tracing'
import EmptyState from 'modules/shares/components/EmptyState'
import { DsmTextEditor } from '../../components'
import { PrivateWorkspaceRouteExtraProps } from 'modules/workspace/containers'
import { WorkspaceSidebarLayoutExtraProps } from 'modules/workspace/components/WorkspaceSidebarLayout/WorkspaceSidebarLayout'

// Styles
import { Wrapper, Logo } from './DesignSystem.styles'

// Utils
import { DesignSystem as DesignSystemType } from '../../types'

// Hooks
import { useGetDesignSystemQuery } from '@sketch/gql-types'
import { useSearch } from 'modules/shares/hooks/useSearch'
import {
  DSDocsEditModeContextProvider,
  useDSDocsEditModeContext,
} from '../../operations/DSDocsEditModeContext'

interface DesignSystemViewRouteProps
  extends RouteProps<'DESIGN_SYSTEM'>,
    PrivateWorkspaceRouteExtraProps {}

type DesignSystemViewProps = DesignSystemViewRouteProps &
  WorkspaceSidebarLayoutExtraProps

// Constants
const defaultEditorContent = {
  content: [
    {
      attrs: {
        level: 1,
      },
      content: [
        {
          text: 'Text editor playground',
          type: 'text',
        },
      ],
      type: 'heading',
    },
    {
      content: [
        {
          text:
            'This is a description for the design system page, you can just click and start writing...',
          type: 'text',
        },
      ],
      type: 'paragraph',
    },
  ],
  type: 'doc',
}

const DesignSystemContent: React.FC<DesignSystemViewProps> = props => {
  const { workspace, match, useOverrideLayoutProps, NavbarPortal } = props
  const { designSystemId, workspaceId } = match.params
  const history = useHistory()
  const { search } = useSearch()
  const isDesignSystemsEditorOn = useFlag('design-systems-editor')
  const { isEditMode } = useDSDocsEditModeContext()

  const {
    data,
    loading: designSystemLoading,
    error: designSystemError,
    fetchMore,
  } = useGetDesignSystemQuery({
    variables: {
      identifier: designSystemId,
      search,
    },
    skip: !workspace,
  })
  const designSystem = data?.designSystem

  useOverrideLayoutProps({
    title: designSystem
      ? `Design System - ${designSystem.name}`
      : 'Design System',
    hideFooter: true,
  })

  if (designSystemLoading && !data) {
    return <LoadingState />
  }

  if (!designSystem) {
    return <ErrorMessage.Generic />
  }

  if (designSystemError) {
    return <GenericErrorView error={designSystemError} />
  }

  const designSystemLibraries = designSystem?.shares.entries || []
  const userIsGuest =
    workspace?.userRole === 'GUEST' || workspace?.userRole === undefined

  const after = designSystem?.shares.meta.after || null
  const loadMoreHandler = handleFetchMore(
    fetchMore,
    ['designSystem', 'shares', 'entries'],
    {
      dataIdFromObject,
      after,
    }
  )
  const totalShareCount = designSystem?.shares.meta.totalCount || 0

  const handleDelete = () => {
    if (!workspaceId) {
      return
    }

    history.push(routes.WORKSPACE_LIBRARIES.create({ workspaceId }))
  }

  const designSystemHasDocumentation = designSystem?.sections?.length > 0

  if (designSystemHasDocumentation || isEditMode) {
    return (
      <DesignSystemWithDocs
        loadMoreHandler={loadMoreHandler}
        designSystem={designSystem as DesignSystemType}
        designSystemLoading={designSystemLoading}
        search={search}
        {...props}
      />
    )
  }

  return (
    <>
      <NavbarPortal>
        {isDesignSystemsEditorOn ? (
          <EditorNavbar
            userIsGuest={userIsGuest}
            designSystem={designSystem as DesignSystemType}
          />
        ) : (
          <ShareNavbar
            userRole={workspace.userRole}
            showViewOptions={Boolean(totalShareCount)}
          />
        )}
      </NavbarPortal>

      <Wrapper>
        <Header
          headerURL={designSystem.header}
          designSystemId={designSystem.identifier}
          userIsGuest={userIsGuest}
        />
        <Flex borderBottom="1px solid" borderColor="border.B" p={10}>
          {designSystem.preview && (
            <Box mr={4}>
              <Logo src={designSystem.preview} />
            </Box>
          )}
          <Flex flexDirection="column">
            <Flex>
              <Text textStyle="header.primary.H" mr={3}>
                {designSystem?.name}
              </Text>
              <DesignSystemDropdown
                deleteCallback={handleDelete}
                designSystem={designSystem as DesignSystemType}
                url={routes.DESIGN_SYSTEM.create({
                  workspaceId,
                  designSystemId: designSystem.identifier,
                })}
                userIsGuest={userIsGuest}
              />
            </Flex>
            <Box mt={1} mb={3}>
              <Text textStyle="copy.tertiary.standard.E">
                {designSystem?.description || 'No description added.'}
              </Text>
            </Box>
            <Text textStyle="copy.quaternary.standard.C">
              Updated <TimeAgo date={designSystem?.updatedAt} />
            </Text>
          </Flex>
        </Flex>

        {isDesignSystemsEditorOn && (
          <Flex borderBottom="1px solid" borderColor="border.B" p={10}>
            <DsmTextEditor content={defaultEditorContent} />
          </Flex>
        )}

        {totalShareCount ? (
          <Flex p={10} flexDirection="column" flex="auto">
            <Text textStyle="header.primary.G" mb={8}>
              Linked Libraries
            </Text>

            {designSystemLoading ? (
              <LoadingState />
            ) : (
              <DocumentItemsLayout
                shares={designSystemLibraries}
                renderDropdown={({ identifier }) => {
                  const share = designSystemLibraries.find(
                    library => library.identifier === identifier
                  )
                  if (!share) {
                    ErrorHandler.shouldNeverHappen(
                      'Library should always be present rendering the DocumentDropdown'
                    )
                    return null
                  }
                  return (
                    <DocumentItemDropdown
                      share={share}
                      workspaceIdentifier={workspace.identifier}
                    />
                  )
                }}
                onLoadMore={loadMoreHandler}
                totalCount={totalShareCount}
                showProjectName={false}
              />
            )}
          </Flex>
        ) : (
          <>
            <Text textStyle="header.primary.G" mb={8} ml={10} mt={10}>
              Linked Libraries
            </Text>
            <EmptyState
              search={search}
              title="No Libraries available to view yet"
              description="Libraries shared with you in this Workspace will appear here"
              icon="link"
            />
          </>
        )}
      </Wrapper>
    </>
  )
}

export const DesignSystem: React.FC<DesignSystemViewProps> = props => (
  <DSDocsEditModeContextProvider>
    <DesignSystemContent {...props} />
  </DSDocsEditModeContextProvider>
)
